import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, CheckSquare } from 'lucide-react';
import './MultiSelectSearchable.css';

const MultiSelectSearchable = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = 'اختر...', 
  searchPlaceholder = 'ابحث...',
  disabled = false,
  loading = false,
  selectAllLabel = 'اختيار الكل'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleSelectAll = () => {
    if (value.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  };

  const handleRemoveItem = (e, itemValue) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== itemValue));
  };

  return (
    <div className="multi-select-searchable" ref={wrapperRef}>
      <div 
        className={`multi-select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
      >
        <div className="multi-select-value">
          {loading ? (
            <span className="placeholder">جاري التحميل...</span>
          ) : value.length === 0 ? (
            <span className="placeholder">{placeholder}</span>
          ) : (
            <div className="selected-tags">
              {value.slice(0, 3).map(v => {
                const option = options.find(opt => opt.value === v);
                return (
                  <span key={v} className="selected-tag">
                    {option?.label || v}
                    <button type="button" onClick={(e) => handleRemoveItem(e, v)}>
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
              {value.length > 3 && (
                <span className="more-tag">+{value.length - 3}</span>
              )}
            </div>
          )}
        </div>
        <ChevronDown size={18} className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </div>

      {isOpen && !disabled && (
        <div className="multi-select-dropdown">
          <div className="multi-select-search">
            <Search size={16} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Select All Button */}
          {!searchTerm && options.length > 0 && (
            <button 
              type="button"
              className="select-all-btn"
              onClick={handleSelectAll}
            >
              <CheckSquare size={16} />
              <span>{value.length === options.length ? 'إلغاء الكل' : selectAllLabel}</span>
            </button>
          )}
          
          <div className="multi-select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <label
                  key={option.value}
                  className={`multi-select-option ${value.includes(option.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                  />
                  <span className="checkmark"></span>
                  <span className="option-label">{option.label}</span>
                </label>
              ))
            ) : (
              <div className="multi-select-empty">
                لا توجد نتائج
              </div>
            )}
          </div>
          
          {value.length > 0 && (
            <div className="multi-select-footer">
              تم اختيار {value.length} من {options.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectSearchable;
